import { CirclePlus, Copy, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { Features } from '~/components/FeatureToggle';
import MenuAction from '~/components/Menu/MenuAction';
import { structureSetApprovalMenuItems } from '~/components/Structures/structureSetApprovalMenuItems';
import StructureSetApprovalStatusConfirmationDialog from '~/components/Structures/StructureSetApprovalStatusConfirmationDialog';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useCopyStructureSetFlow } from '~/hooks/structure-set/useCopyStructureSetFlow';
import useCreateStructureSet from '~/hooks/structure-set/useCreateStructureSet';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    showSensitiveInfoState,
    WorkflowTypes,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import CopyStructureSetDialog from '../StructureSet/CopyStructureSetDialog/CopyStructureSetDialog';

/**
 * The menu rows that act on structure sets, and the dialogs they open:
 * create and generate a set for the primary image, then, while a set is
 * selected, the approval statuses it is not in, Rename, Copy to... and
 * Delete Structure Set, last. Each approval change and the delete ask
 * first; Approve All warns about rejected structures. Rename is refused,
 * with the reason, on an approved set and while patient info is hidden,
 * since the name editor would print the name in plaintext. `close` closes
 * the menu before a row runs; `onRename` opens the set's name editor.
 *
 * The dialogs must stay mounted while the menu is closed.
 */
export default function useStructureSetActions({
    close,
    onRename,
}: {
    close: () => void;
    onRename: () => void;
}): { items: ReactNode[]; dialogs: ReactNode } {
    const intl = useIntl();
    const structureSet = useRecoilValue(currentStructureSetState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const primaryImage = useSelectedPrimaryImage();
    const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const closeCopyDialog = useCallback(() => setIsCopyDialogOpen(false), []);
    const { patient, domainStructureSet, sourceImage, handleConfirmCopy } =
        useCopyStructureSetFlow(structureSet ?? null, closeCopyDialog);
    const { setStructureSetApprovalStatus } = useStructureSet();
    const createStructureSet = useCreateStructureSet();
    const { deleteStructureSet } = useDeleteDataset();
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const openApprovalConfirmation = useSetRecoilState(
        openedStructureSetApprovalConfirmationDialogState
    );
    const isGenerateEnabled = AppConfig.isFeatureEnabled(
        Features.CONTOUR_WORKFLOW
    );
    const lockedReason =
        structureSet?.approvalStatus === DICOMApprovalStatus.APPROVED
            ? intl.formatMessage({ id: 'datasetPicker.approvedSetLocked' })
            : undefined;
    const renameRefusedReason =
        lockedReason ??
        (showSensitiveInfo
            ? undefined
            : intl.formatMessage({ id: 'datasetPicker.patientInfoHidden' }));

    const runAndClose = (run: () => void) => () => {
        close();
        run();
    };

    const handleDelete = () => {
        if (domainStructureSet) {
            deleteStructureSet(domainStructureSet);
        }
        setIsDeleteDialogOpen(false);
    };

    const items: ReactNode[] = [
        <MenuAction
            key="create"
            icon={<CirclePlus />}
            label={<FormattedMessage id="createStructureSet" />}
            onClick={runAndClose(createStructureSet)}
            data-testid="new-structureset-button"
        />,
        isGenerateEnabled && (
            <MenuAction
                key="generate"
                icon={<Sparkles />}
                label={<FormattedMessage id="generateStructureSet" />}
                onClick={runAndClose(() =>
                    setWorkflowModal((state) => ({
                        ...state,
                        open: true,
                        page: WorkflowTypes.CONTOUR,
                    }))
                )}
                disabled={!selectedPatientId || !primaryImage}
                data-testid="generate-structureset-button"
            />
        ),
        ...(structureSet
            ? [
                  ...structureSetApprovalMenuItems(structureSet, (request) => {
                      close();
                      openApprovalConfirmation(request);
                  }),
                  <MenuAction
                      key="rename"
                      icon={<Pencil />}
                      label={<FormattedMessage id="rename" />}
                      onClick={runAndClose(onRename)}
                      disabledReason={renameRefusedReason}
                      data-testid="rename-structureset-button"
                  />,
                  <MenuAction
                      key="copy"
                      icon={<Copy />}
                      label={<FormattedMessage id="structureSet.copy" />}
                      onClick={runAndClose(() => setIsCopyDialogOpen(true))}
                      disabled={!domainStructureSet || !sourceImage}
                      data-testid="copy-structure-set-menu-item"
                  />,
                  <MenuAction
                      key="delete"
                      icon={<Trash2 />}
                      label={<FormattedMessage id="deleteStructureSet" />}
                      onClick={runAndClose(() => setIsDeleteDialogOpen(true))}
                      destructive
                      data-testid="delete-structureset-button"
                  />,
              ]
            : []),
    ].filter(Boolean);

    const dialogs = (
        <>
            {isCopyDialogOpen && patient && sourceImage && structureSet && (
                <CopyStructureSetDialog
                    open={isCopyDialogOpen}
                    onClose={closeCopyDialog}
                    onConfirm={handleConfirmCopy}
                    sourceStructureSet={structureSet}
                    patient={patient}
                    sourceImage={sourceImage}
                />
            )}
            <ConfirmationDialog
                open={isDeleteDialogOpen}
                title={<FormattedMessage id="deleteStructureSet" />}
                message={<FormattedMessage id="deleteStructureSetConfirm" />}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogOpen(false)}
                confirmText={<FormattedMessage id="delete" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="error"
            />
            <StructureSetApprovalStatusConfirmationDialog
                onApprovalStatusChange={setStructureSetApprovalStatus}
            />
        </>
    );

    return { items, dialogs };
}
